import {
  IMultipleRowActions,
  Modal,
  Table,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import StorageIcon from "@mui/icons-material/Storage";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReplayIcon from "@mui/icons-material/Replay";
import { Chip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import MainApi from "../../../../services/mainApi.service";
import MacroFilter from "./MacroFilter";
import ServiceReceivedDetailsModal from "./ServiceReceivedDetailsModal";
import { IEncounterDataZ } from "../../../../models/ecounter-data-z";
import { restartServiceReceivedRetries } from "../../../../services/encounterDataZ.service";
import { ReceivedDataTable } from "./ReceivedDataTable";
import { isJsonString } from "../../../../utils/conversions";
import { IFilterParams } from "./models";
import { format } from "date-fns";
import { ptBR as dateFnsPtBR, enUS, es } from "date-fns/locale";

const STATUS_ITEMS: Record<
  string,
  { color: "warning" | "info" | "success" | "error" | "default"; key: string }
> = {
  INTEGRADO: { color: "success", key: "integrated" },
  RECEBIDO: { color: "info", key: "received" },
  PENDENTE: { color: "warning", key: "pending" },
  ERRO: { color: "error", key: "error" },
};

const ENCOUNTER_DATA_Z_ROUTE =
  process.env.REACT_APP_EXTERNAL + "/api/v1/EncounterDataZ";

const encounterDataZService = new MainApi<IEncounterDataZ>(
  ENCOUNTER_DATA_Z_ROUTE,
);

const translationPath = "page.4uBus.logs.serviceReceived.";

const ServiceReceived = () => {
  const { setMessage } = useNotificationContext();

  const { sendRequest: restartServiceReceivedRetriesReq } = useFetch(
    restartServiceReceivedRetries,
  );

  const [refresh, setRefresh] = useState(0);

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const [receivedDataModalOpen, setReceivedDataModalOpen] = useState(false);

  const [selectedRow, setSelectedRow] = useState<IEncounterDataZ | null>(null);

  const today = useMemo(() => new Date(), []);

  const [filterParams, setFilterParams] = useState<IFilterParams>({
    encounterNumber: "",
    encounterNumberOS: "",
    individualIdentifierOs: "",
    individualDocumentOs: "",
    individualNameOs: "",
    individualIdentifier: "",
    status: "",
    createdStart: today,
    createdEnd: today,
    establishmentCodeOs: "",
    encounterDateEntryStart: null,
    encounterDateEntryEnd: null,
    encounterDateEntryOption: null
  });

  const { t, i18n } = useTranslation();

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return format(date, "Pp", {
      locale:
        i18n.language === "pt-BR"
          ? dateFnsPtBR
          : i18n.language === "en-US"
            ? enUS
            : es,
    }).replaceAll(",", "");
  };

  const columns: GridColDef<IEncounterDataZ>[] = [
    {
      field: "identifier",
      headerName: t(translationPath + "identifier") || "Identifier",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "establishmentCodeOs",
      headerName:
        t(translationPath + "establishment_code") ||
        "External Establishment Code",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "encounterNumber",
      headerName:
        t(translationPath + "generated_service_number") ||
        "Generated Service Number",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "individualDocumentOs",
      headerName: t(translationPath + "external_cpf") || "External CPF",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "status",
      headerName: t(translationPath + "status_item") || "Status",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (!params.value) return "-";

        const status = params.value.toUpperCase();

        const getStatusConfig = (status: string) => {
          return (
            STATUS_ITEMS[status] || {
              color: "warning" as const,
              key: "pending",
            }
          );
        };

        const config = getStatusConfig(status);

        return (
          <Chip
            label={t(translationPath + `status.${config.key}`)}
            color={config.color}
            size="small"
          />
        );
      },
    },
    {
      field: "retries",
      headerName: t(translationPath + "retries") || "Tentativas",
      flex: 0.5,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? 0;
      },
    },
    {
      field: "encounterNumberOs",
      headerName:
        t(translationPath + "external_service_number") ||
        "External Service Number",
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
        {
      field: "encounterDateEntry",
      headerName:
        t(translationPath + "encounter_date_entry") ||
        "Encounter Date Entry",
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return formatDate(params.value) ?? "-";
      },
    },
    {
      field: "individualIdentifierOs",
      headerName:
        t(translationPath + "external_person_code") || "External Person Code",
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "individualIdentifier",
      headerName:
        t(translationPath + "linked_person_code") ||
        "Linked/Generated Person Code",
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "individualNameOs",
      headerName:
        t(translationPath + "external_person_name") || "External Person Name",
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
  ];

  const params = useMemo(
    () => ({
      refresh,
      ...filterParams,
      createdStart: filterParams?.createdStart
        ? new Date(filterParams.createdStart.setHours(15))
            .toISOString()
            .substring(0, 10)
        : undefined,
      createdEnd: filterParams?.createdEnd
        ? new Date(filterParams.createdEnd.setHours(15))
            .toISOString()
            .substring(0, 10)
        : undefined,
      encounterDateEntryStart: filterParams?.encounterDateEntryStart
        ? new Date(filterParams.encounterDateEntryStart.setHours(15))
            .toISOString()
            .substring(0, 10)
        : undefined,
      encounterDateEntryEnd: filterParams?.encounterDateEntryEnd
        ? new Date(filterParams.encounterDateEntryEnd.setHours(15))
            .toISOString()
            .substring(0, 10)
        : undefined,
      // Estado de UI do filtro, não deve ir para a query.
      encounterDateEntryOption: undefined,
    }),
    [refresh, filterParams],
  );

  const onRefreshHandler = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const handleRestartServiceReceivedRetries = useCallback(
    async (identifier: number) => {
      const { success } = await restartServiceReceivedRetriesReq({
        identifier,
      });

      if (success) {
        onRefreshHandler();
        setMessage({
          message: t(translationPath + "resetedSuccess"),
          type: "success",
        });
      }
    },
    [onRefreshHandler, restartServiceReceivedRetriesReq, setMessage, t],
  );

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      setRefresh((prev) => prev + 1);
    },
    [],
  );

  const handleCloseDetailsModal = useCallback(() => {
    setDetailsModalOpen(false);
    setSelectedRow(null);
  }, []);

  const handleCloseReceivedDataModal = useCallback(() => {
    setReceivedDataModalOpen(false);
    setSelectedRow(null);
  }, []);

  const multipleRowsActions = useMemo<IMultipleRowActions[]>(
    () => [
      {
        action: async (ids) => {
          const identifiers = ids
            .map((id) => Number(id))
            .filter((id) => !Number.isNaN(id));

          if (identifiers.length === 0) return;

          const { success } = await restartServiceReceivedRetriesReq({
            identifierList: identifiers,
          });

          if (success) {
            onRefreshHandler();
            setMessage({
              message: t(`${translationPath}resetedSuccess`),
              type: "success",
            });
          }
        },
        icon: <ReplayIcon fontSize="small" />,
        text: t(translationPath + "resync") || "Forçar reenvio",
      },
    ],
    [onRefreshHandler, restartServiceReceivedRetriesReq, setMessage, t],
  );

  return (
    <>
      <Modal
        open={detailsModalOpen}
        onClose={handleCloseDetailsModal}
        maxDialogWidth="lg"
      >
        {selectedRow && (
          <ServiceReceivedDetailsModal
            serviceReceivedData={selectedRow}
            onClose={handleCloseDetailsModal}
          />
        )}
      </Modal>

      <Modal
        open={receivedDataModalOpen}
        onClose={handleCloseReceivedDataModal}
        maxDialogWidth="lg"
      >
        {selectedRow && (
          <ReceivedDataTable
            jsonContentData={JSON.parse(selectedRow.jsonContent)}
          />
        )}
      </Modal>

      <Table<IEncounterDataZ>
        title={t(translationPath + "title")}
        columns={columns}
        service={encounterDataZService}
        searchInputPlaceHolder={
          t(translationPath + "search_placeholder") || "Search..."
        }
        showDefaultMacroFilters={false}
        showButton={false}
        showDeleteButton={false}
        defaultActions={true}
        getRowId={(row) => (row.identifier ? row.identifier : 0)}
        multipleRowsActions={multipleRowsActions}
        rowActions={(params) => [
          {
            action: () => {
              setSelectedRow(params.row);
              setDetailsModalOpen(true);
            },
            icon: <VisibilityIcon fontSize="small" />,
            text: t(translationPath + "view_details") || "See Details",
          },
          isJsonString(params.row.jsonContent)
            ? {
                action: () => {
                  setSelectedRow(params.row);
                  setReceivedDataModalOpen(true);
                },
                icon: <StorageIcon fontSize="small" />,
                text:
                  t(translationPath + "view_received_data") ||
                  "View Received Data",
              }
            : null,
          params.row.status === "ERRO"
            ? {
                action: () => {
                  handleRestartServiceReceivedRetries(params.row.identifier);
                },
                icon: <ReplayIcon fontSize="small" />,
                text: t(translationPath + "resync") || "Reiniciar tentativas",
              }
            : null,
        ]}
        serviceProps={params}
        macroFilters={useCallback(
          () => (
            <MacroFilter
              filterParams={filterParams}
              onFilterParams={handleFilterChange}
            />
          ),
          [filterParams, handleFilterChange],
        )}
      />
    </>
  );
};

export default ServiceReceived;
